// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component, Inject, Prop, Vue } from 'vue-property-decorator';
import NoSSR from 'vue-no-ssr';
import VueEasyLightbox from 'vue-easy-lightbox';
import { BootstrapButton, BootstrapIcon, BootstrapImage, BootstrapLink } from '../../../dsl/front';
import { ShareType } from '../../../services';
import { AppTheme, ImageRatio } from '../../_contracts';
import { prepareImage } from '../../_filters';
import { ArticleActions } from '../ArticleActions';
import { ArticleAuthor } from '../ArticleAuthor';
import { ArticleShare } from '../ArticleShare';
import { ArticleSocials } from '../ArticleSocials';
import { Gallery } from '../Gallery';
import { ImageWithCaption } from '../ImageWithCaption';
import { LineHeader } from '../LineHeader/ui';
import { PlayIcon } from '../PlayIcon';
import { AdBannerModuleUi } from '../../AdBanner/ui';
import ArticleHeaderVideo from './ArticleHeaderVideo/ArticleHeaderVideo';
/**
 * @author Sebastian Dziechciarz <sebastian.dziechciarz@movecloser.pl>
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
let ArticleHeader = class ArticleHeader extends Vue {
    constructor() {
        super(...arguments);
        this.AppTheme = AppTheme;
        /**
         * Article author's avatar.
         */
        this.authorAvatar = null;
        /**
         * Article author's full name.
         */
        this.authorName = '';
        /**
         * Social channels available in the article.
         */
        this.enabledServices = ['facebook', 'twitter', 'linkedin', 'instagram'];
        /**
         * Determines whether image is displayed in full screen mode
         */
        this.isFullScreenPreviewOpen = false;
        /**
         * Article's thumbnail.
         */
        this.thumbnail = null;
        this.maxWidth = undefined;
        this.forceHideAd = false;
        this.emptyAds = () => {
            console.log('Empty cover Ad');
        };
        /**
         * Construct target link
         */
        this.buildLink = (nav) => {
            let url = '';
            const currentSlug = nav.parent;
            if (!currentSlug.parent) {
                if (currentSlug.slug === '/') {
                    return '';
                }
                return currentSlug.slug;
            }
            url = currentSlug.slug;
            return [url, ...this.buildLink(currentSlug), '/'].reverse().length > 0
                ? [url, ...this.buildLink(currentSlug), '/'].reverse().join('')
                : [url, ...this.buildLink(currentSlug), '/'].toString();
        };
    }
    watchEvents() {
        if (!this.eventBus) {
            return;
        }
        this.eventBus.handle('ui:video-player.play', this.hideAds);
    }
    hideAds() {
        this.forceHideAd = true;
    }
    /**
     * Article actions.
     */
    get actions() {
        const actions = [
            {
                callback: () => {
                    window.print();
                },
                icon: 'print',
                label: 'Drukuj',
                hideForMobile: true
            }
        ];
        if (this.content.pdf && this.pdfService) {
            actions.push({
                callback: () => {
                    if (this.pdfService && this.content.pdf) {
                        return this.pdfService.downland(this.content.pdf);
                    }
                },
                disabled: !this.content.pdf.path,
                icon: 'download',
                label: 'PDF'
            });
        }
        return actions;
    }
    get shouldHideDescription() {
        if (!this.content.cover.hideDescription) {
            return false;
        }
        return this.content.cover.hideDescription;
    }
    /**
     * Closes full screen view of image
     */
    closeFullScreenPreview() {
        this.isFullScreenPreviewOpen = false;
    }
    /**
     * Opens image in the full screen mode
     */
    openFullScreenPreview() {
        this.isFullScreenPreviewOpen = true;
    }
    /**
     * Checking that content has gallery
     */
    get hasGallery() {
        return typeof this.content.cover === 'object' && Array.isArray(this.content.cover.gallery) && this.content.cover.gallery !== null;
    }
    /**
     * Determines whether the article has its corresponding video.
     */
    get hasVideo() {
        return !!this.content.cover.video && ((typeof this.content.cover.video.url === 'string' && this.content.cover.video.url.length > 0) ||
            (typeof this.content.cover.video.onNetworkUrl === 'string' && this.content.cover.video.onNetworkUrl.length > 0) ||
            (!!this.content.cover.video.file && typeof this.content.cover.video.file === 'object' &&
                (!!this.content.cover.video.file.url || !!this.content.cover.video.file.m3u8)));
    }
    /**
     * Checking that content has cover photo - Thumbnail
     */
    get hasThumbnail() {
        return typeof this.content.cover === 'object' && typeof this.content.cover.image === 'object' && this.content.cover.image !== null;
    }
    /**
     * Checking if cover photo has an overriden caption
     */
    get overridenCaption() {
        return (typeof this.content.cover === 'object' && this.content.cover.override === true) ? this.content.cover.caption : undefined;
    }
    /**
     * Get images list to gallery.
     */
    get images() {
        if (!this.hasGallery) {
            return [];
        }
        if (!this.content.cover.gallery) {
            return [];
        }
        return this.content.cover.gallery;
    }
    get baseVideoMetadata() {
        let name = this.overridenCaption ? this.overridenCaption : (this.thumbnail ? this.thumbnail.caption : '');
        if (!name) {
            name = this.content.meta.title;
        }
        return {
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            name,
            thumbnailUrl: this.thumbnail ? this.thumbnail.src : '',
            uploadDate: this.content.publicationDate
        };
    }
    get authorLink() {
        if (!this.content || !this.content.author || !this.content.author.url) {
            return '';
        }
        return this.content.author.url;
    }
    /**
     * TODO: Description.
     */
    get shareButtons() {
        return [
            {
                callback: () => {
                    this.onSocialClick(ShareType.Facebook);
                },
                icon: 'facebook',
                label: this.$t('share.facebook')
            },
            {
                callback: () => {
                    this.onSocialClick(ShareType.Twitter);
                },
                icon: 'x',
                label: this.$t('share.twitter')
            },
            {
                callback: () => {
                    this.onSocialClick(ShareType.LinkedIn);
                },
                icon: 'linkedin',
                label: this.$t('share.linkedin')
            },
            {
                callback: () => {
                    return this.onSocialClick(ShareType.CopyLink);
                },
                clickedIcon: 'checkmark',
                icon: 'link',
                label: this.$t('share.link')
            },
            {
                callback: () => {
                    this.onSocialClick(ShareType.Email);
                },
                clickedIcon: 'checkmark',
                icon: 'email',
                label: this.$t('share.email')
            }
        ];
    }
    /**
     * TODO: Description.
     */
    get shareUrl() {
        if (typeof document === 'undefined') {
            return this.content.urlPath;
        }
        if (!this.content.urlPath) {
            return document.location.origin + document.location.pathname;
        }
        if (this.content.urlPath.indexOf('http') >= 0) {
            return this.content.urlPath;
        }
        return document.location.origin + '/' + this.content.urlPath;
    }
    get videoAdsTag() {
        return this.content.contentProperties.videoAdsTag;
    }
    /**
     * TODO: Description.
     */
    onSocialClick(social) {
        this.shareService.share(social, {
            url: this.shareUrl,
            meta: this.content.meta
        });
    }
    /**
     * Resolves the article's author from the related data.
     */
    resolveAuthor() {
        if (this.content.author === null) {
            return;
        }
        const { avatar, firstName, lastName } = this.content.author;
        if (avatar) {
            try {
                this.authorAvatar = prepareImage(avatar, ImageRatio.Square, 28, this.isLazy);
            }
            catch (e) {
                console.warn(e);
            }
        }
        this.authorName = `${firstName} ${lastName}`;
    }
    /**
     * Resolves the article's thumbnail from the related data.
     */
    resolveThumbnail() {
        if (!this.hasThumbnail) {
            return;
        }
        this.thumbnail = prepareImage(this.content.cover.image, ImageRatio.Standard169, this.maxWidth, this.isLazy);
    }
};
__decorate([
    Prop({
        type: Object,
        required: true
    })
], ArticleHeader.prototype, "content", void 0);
__decorate([
    Prop({
        type: Object,
        required: false
    })
], ArticleHeader.prototype, "parent", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], ArticleHeader.prototype, "eventBus", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: ''
    })
], ArticleHeader.prototype, "fallbackImage", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], ArticleHeader.prototype, "isMobile", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], ArticleHeader.prototype, "isLazy", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], ArticleHeader.prototype, "isRelated", void 0);
__decorate([
    Prop({
        type: Object,
        required: false,
        default: null
    })
], ArticleHeader.prototype, "pdfService", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], ArticleHeader.prototype, "shareService", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], ArticleHeader.prototype, "relatedService", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], ArticleHeader.prototype, "showActions", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], ArticleHeader.prototype, "windowService", void 0);
__decorate([
    Inject({
        from: 'theme',
        default: AppTheme.Default
    })
], ArticleHeader.prototype, "theme", void 0);
ArticleHeader = __decorate([
    Component({
        name: 'ArticleHeader',
        components: {
            ArticleActions,
            ArticleAuthor,
            ArticleHeaderVideo,
            ArticleShare,
            ArticleSocials,
            BootstrapButton,
            BootstrapIcon,
            BootstrapImage,
            BootstrapLink,
            Gallery,
            ImageWithCaption,
            LineHeader,
            NoSSR,
            PlayIcon,
            VueEasyLightbox,
            AdBannerModuleUi
        },
        mounted() {
            this.watchEvents();
        },
        created() {
            this.maxWidth = this.isMobile ? 768 : 800;
            if (this.content.theme === AppTheme.PremiumZone) {
                this.maxWidth = this.isMobile ? 768 : 1220;
            }
            this.resolveAuthor();
            this.resolveThumbnail();
        },
        template: `
    <header class="article-header">

    <!-- Theme: PremiumZone -->
    <template v-if="theme === AppTheme.PremiumZone">
      <div class="row justify-content-center">
        <div class="col-12 col-md-10">
          <!-- Article's parent -->
          <div class="article-header__parent d-flex justify-content-center">
            <BootstrapLink v-if="content.parent && content.parent.name.length"
                           :link="{ target: { path: '/'+content.categoryUrl } }"
                           class="text-decoration-none">
              {{ content.parent.name }}
            </BootstrapLink>
          </div>

          <!-- Article's title -->
          <h1 class="article-header__title fs-3 fs-lg-1 text-md-center">
            {{ content.title }}
          </h1>
        </div>

        <!-- Cover image -->
        <div class="col-12">
          <div class="article-header__image">
            <ImageWithCaption v-if="hasThumbnail" :image="thumbnail" :isLazy="isLazy"
                              :fetchPriority="!isLazy"
                              class="article-header__image__cover --fixed-ratio --21x9">
              <ArticleHeaderVideo v-if="hasVideo"
                                  :video="content.cover.video"
                                  :videoSettings="content.cover.videoSettings"
                                  :videoAdsTag="videoAdsTag"
                                  :noThumbnail="thumbnail === null"
                                  :baseVideoMetadata="baseVideoMetadata"
                                  :eventBus="eventBus"
                                  :siteDomain="content.contentProperties.site.address"/>
            </ImageWithCaption>
          </div>
        </div>
        <!-- END | Cover image -->

        <!-- Article's lead -->
        <div v-if="content.lead" v-html="content.lead" class="col-12 col-md-8 article-header__lead"/>
      </div>

    </template>
    <!-- END | Theme: PremiumZone -->

    <!-- Theme: Default or other sites -->
    <template v-else>
      <template v-if="hasVideo">
        <div class="article-header__wrap">
          <!-- Article's parent -->
          <BootstrapLink v-if="content.parent && content.parent.name.length"
                         :link="{ target: { path: '/' + content.categoryUrl } }"
                         class="text-decoration-none">
            <LineHeader :isBold="true">
              {{ content.parent.name }}
            </LineHeader>
          </BootstrapLink>
          <!-- END | Article's parent -->
          <!--Cover image / Single image -->
          <div v-if="hasThumbnail || fallbackImage" class="article-header__image">
            <template v-if="hasThumbnail">
              <ImageWithCaption v-if="thumbnail" :image="thumbnail"
                                :hideDescription="true" :isLazy="isLazy"
                                :hideAuthor="true"
                                :fetchPriority="!isLazy"
                                @click="openFullScreenPreview">
                <ArticleHeaderVideo :video="content.cover.video"
                                    :videoSettings="content.cover.videoSettings"
                                    :videoAdsTag="videoAdsTag"
                                    :noThumbnail="thumbnail === null"
                                    :baseVideoMetadata="baseVideoMetadata"
                                    :eventBus="eventBus"
                                    :siteDomain="content.contentProperties.site.address"/>
                <AdBannerModuleUi v-if="content.ads" class="article-header__ad"
                                  v-bind="{eventBus, fallbackImage, onEmptyCallback: emptyAds, module: content.ads, parent, pdfService, shareService, theme, windowService, isMobile, relatedService, forceHide: forceHideAd}"/>

              </ImageWithCaption>
            </template>
            <template v-else>
              <BootstrapImage :src="fallbackImage"/>
              <ArticleHeaderVideo :video="content.cover.video"
                                  :videoSettings="content.cover.videoSettings"
                                  :videoAdsTag="videoAdsTag"
                                  :noThumbnail="thumbnail === null"
                                  :baseVideoMetadata="baseVideoMetadata"
                                  :eventBus="eventBus"
                                  :siteDomain="content.contentProperties.site.address"/>
              <AdBannerModuleUi v-if="content.ads" class="article-header__ad"
                                v-bind="{eventBus, fallbackImage, onEmptyCallback: emptyAds, module: content.ads, parent, pdfService, shareService, theme, windowService, isMobile, relatedService, forceHide: forceHideAd}"/>
            </template>
            <NoSSR>
              <VueEasyLightbox v-if="!hasVideo && thumbnail"
                               :visible="isFullScreenPreviewOpen" :imgs="thumbnail.fullscreen"
                               @hide="closeFullScreenPreview"/>
            </NoSSR>
          </div>
          <!-- END | Cover image -->
          <!-- Title -->
          <h1 class="article-header__title fs-3 fs-lg-1">{{ content.title }}</h1>
        </div>
        <div class="row justify-content-between mt-5 mb-4">
          <!-- Author -->
          <div class="col-auto">
            <div v-if="authorLink">
              <BootstrapLink :link="{ target: authorLink }" class="text-decoration-none">
                <ArticleAuthor v-bind="{ publicationDate: content.publicationDate }"
                               :hideDate="content.options && content.options.hidePublicationDate"
                               :name="authorName"
                               :avatar="authorAvatar !== null ? authorAvatar : undefined"
                               :showInitialsWhenNoAvatar="true"/>
              </BootstrapLink>
            </div>
            <div v-else>
              <ArticleAuthor v-bind="{ publicationDate: content.publicationDate }"
                             :hideDate="content.options && content.options.hidePublicationDate"
                             :name="authorName"
                             :avatar="authorAvatar !== null ? authorAvatar : undefined"
                             :showInitialsWhenNoAvatar="true"/>
            </div>
          </div>

          <!-- Print, download & social buttons -->
          <div class="col-auto d-flex justify-content-between align-items-center">
            <ArticleActions v-if="showActions" class="me-lg-3" v-bind="{ actions }"/>
            <ArticleSocials v-if="showActions" class="d-none d-lg-block" v-bind="{ enabledServices }"
                            :onClick="onSocialClick"/>
          </div>
        </div>
        <ArticleShare v-if="showActions" v-bind="{ shareButtons }" :isHidden="false"/>
        <div v-if="content.lead" v-html="content.lead" class="article-header__lead"/>

      </template>
      <template v-else>
        <!-- Article's parent -->
        <BootstrapLink v-if="content.parent && content.parent.name.length"
                       :link="{ target: { path: '/' + content.categoryUrl } }"
                       class="text-decoration-none">
          <LineHeader :isBold="true">
            {{ content.parent.name }}
          </LineHeader>
        </BootstrapLink>
        <!-- END | Article's parent -->
        <!-- Title -->
        <h1 class="article-header__title fs-3 fs-lg-1">{{ content.title }}</h1>

        <div class="row justify-content-between">
          <!-- Author -->
          <div class="col-auto">
            <div v-if="authorLink">
              <BootstrapLink :link="{ target: authorLink }" class="text-decoration-none">
                <ArticleAuthor v-bind="{ publicationDate: content.publicationDate }"
                               :hideDate="content.options && content.options.hidePublicationDate"
                               :name="authorName"
                               :avatar="authorAvatar !== null ? authorAvatar : undefined"
                               :showInitialsWhenNoAvatar="true"/>
              </BootstrapLink>
            </div>
            <div v-else>
              <ArticleAuthor v-bind="{ publicationDate: content.publicationDate }"
                             :hideDate="content.options && content.options.hidePublicationDate"
                             :name="authorName"
                             :avatar="authorAvatar !== null ? authorAvatar : undefined"
                             :showInitialsWhenNoAvatar="true"/>
            </div>
          </div>

          <!-- Print, download & social buttons -->
          <div class="col-auto d-flex justify-content-between align-items-center">
            <ArticleActions v-if="showActions" class="me-lg-3" v-bind="{ actions }"/>
            <ArticleSocials v-if="showActions" class="d-none d-lg-block" v-bind="{ enabledServices }"
                            :onClick="onSocialClick"/>
          </div>
        </div>
        <div class="article-header__wrap">
          <!--Cover image / Gallery -->
          <template v-if="hasGallery">
            <Gallery :images="images" class="article-header__gallery" :maxWidth="maxWidth"
                     :windowService="windowService"
                     fullScreen/>
          </template>
          <!-- END | Cover image / Gallery -->
          <!--Cover image / Single image -->
          <template v-else>
            <div v-if="hasThumbnail || fallbackImage" class="article-header__image">
              <template v-if="hasThumbnail">
                <ImageWithCaption v-if="thumbnail" :image="thumbnail" :overridenCaption="overridenCaption"
                                  :hideDescription="shouldHideDescription" :isLazy="isLazy"
                                  :fetchPriority="!isLazy"
                                  @click="openFullScreenPreview">
                  <AdBannerModuleUi v-if="content.ads" class="article-header__ad"
                                    v-bind="{eventBus, fallbackImage, onEmptyCallback: emptyAds, module: content.ads, parent, pdfService, shareService, theme, windowService, isMobile, relatedService, forceHide: forceHideAd}"/>

                </ImageWithCaption>
              </template>
              <template v-else>
                <BootstrapImage :src="fallbackImage"/>
                <AdBannerModuleUi v-if="content.ads" class="article-header__ad"
                                  v-bind="{eventBus, fallbackImage, onEmptyCallback: emptyAds, module: content.ads, parent, pdfService, shareService, theme, windowService, isMobile, relatedService, forceHide: forceHideAd}"/>
              </template>

              <NoSSR>
                <VueEasyLightbox v-if="!hasVideo && thumbnail"
                                 :visible="isFullScreenPreviewOpen" :imgs="thumbnail.fullscreen"
                                 @hide="closeFullScreenPreview"/>
              </NoSSR>
            </div>
          </template>
          <!-- END | Cover image -->
        </div>
        <ArticleShare v-if="showActions" v-bind="{ shareButtons }" :isHidden="false"/>
        <div v-if="content.lead" v-html="content.lead" class="article-header__lead"/>
      </template>
    </template>
    <!-- END | Theme: Default or other sites -->

    </header>
  `
    })
], ArticleHeader);
export { ArticleHeader };
export default ArticleHeader;
